import { useCallback, useState, type ReactNode } from 'react';
import { OrbisChatBubble } from './OrbisChatBubble';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

const SIDEBAR_KEY = 'orbis:sidebar-collapsed';

/** Read the persisted sidebar state up-front so no first-render flash. */
function initialCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(SIDEBAR_KEY) === '1';
}

interface AppShellProps {
  children: ReactNode;
}

/**
 * App chrome for the authenticated experience — the constant Topbar + Sidebar
 * over the WisdomX server-room backdrop, matching the Figma "Home" frame
 * (node 11050:1520). The Topbar and Sidebar are translucent glass; the
 * background image shows through behind the content area.
 *
 * The shell is pinned to exactly one viewport (`h-screen` + `overflow-hidden`)
 * rather than `min-h-screen`, so only `<main>` scrolls and the chrome stays
 * put. With `min-h-screen` the shell grew to fit its tallest child, which made
 * the document itself scroll and carried the Topbar and Sidebar off-screen.
 *
 * Two things this relies on: every flex ancestor of the scroll area needs
 * `min-h-0` (a flex item's default `min-height: auto` refuses to shrink below
 * its content, which silently defeats the overflow), and route content should
 * size with `min-h-full`, not `min-h-screen`.
 */
export function AppShell({ children }: AppShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(initialCollapsed);
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((c) => {
      const next = !c;
      try {
        window.localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
      } catch {
        // localStorage can throw in incognito / storage-quota scenarios; the
        // toggle still works for the session.
      }
      return next;
    });
  }, []);

  return (
    <div className="relative flex h-screen flex-col overflow-hidden bg-[#0F172A] text-white">
      {/*
       * Background scene. Blurred and scaled up: at full sharpness the
       * server-room photo competes with the panels for attention and its
       * detail bleeds through the translucent cards, which reads as visual
       * noise between sections. `scale-110` overscans so the blur radius has
       * pixels to sample at the edges instead of fading to transparent.
       */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 scale-110 bg-cover bg-center blur-[14px]"
        style={{ backgroundImage: 'url(/wisdomx-bg.png)' }}
      />
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 bg-[#050A24]/65" />

      <div className="relative z-10 flex h-full min-h-0 flex-col">
        <Topbar sidebarCollapsed={sidebarCollapsed} onToggleSidebar={toggleSidebar} />
        <div className="flex min-h-0 flex-1">
          <Sidebar collapsed={sidebarCollapsed} />
          <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">{children}</main>
        </div>
      </div>

      {/* Non-functional GenUI preview bubble, bottom-right of every
          authenticated page. Signals the coming chat surface ahead of the
          backend endpoint — see docs/WisdomX_GenUI_Brief.docx Section 3.
          Rendered here in the shell (not per-route) so it persists across
          navigation and cannot double-render. */}
      <OrbisChatBubble />
    </div>
  );
}
